import { useState } from "react";
import { Check, ChevronsUpDown, X } from "lucide-react";

import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from "@/components/ui/command";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { cn } from "@/lib/utils";
import type { MetadataOption } from "@/hooks/use-itsm-metadata";

interface Props {
  options: MetadataOption[];
  value: string[];
  onChange: (value: string[]) => void;
  isLoading?: boolean;
  placeholder?: string;
  allowCustom?: boolean;
}

/** Multi-select of provider metadata, rendered as removable chips. `modal`
 *  so it works inside a Dialog. Allows free-typed values as a fallback. */
export function MetadataMultiSelect({
  options,
  value,
  onChange,
  isLoading,
  placeholder = "Add...",
  allowCustom = true,
}: Props) {
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState("");

  const labelFor = (v: string) => options.find((o) => o.value === v)?.label ?? v;
  const toggle = (v: string) =>
    { onChange(value.includes(v) ? value.filter((x) => x !== v) : [...value, v]); };
  const remove = (v: string) => { onChange(value.filter((x) => x !== v)); };

  const showCreate =
    allowCustom &&
    search.trim() &&
    !options.some((o) => o.value.toLowerCase() === search.trim().toLowerCase()) &&
    !value.includes(search.trim());

  return (
    <div className="space-y-1.5">
      {value.length > 0 && (
        <div className="flex flex-wrap gap-1">
          {value.map((v) => (
            <Badge key={v} variant="secondary" className="gap-1 pr-1 font-normal">
              {labelFor(v)}
              <button
                type="button"
                onClick={() => { remove(v); }}
                className="hover:bg-muted-foreground/20 rounded-sm p-0.5"
                aria-label={`Remove ${labelFor(v)}`}
              >
                <X className="h-3 w-3" />
              </button>
            </Badge>
          ))}
        </div>
      )}
      <Popover open={open} onOpenChange={setOpen} modal>
        <PopoverTrigger asChild>
          <Button
            variant="outline"
            size="sm"
            role="combobox"
            className="h-8 w-full justify-between font-normal"
          >
            <span className="text-muted-foreground">
              {isLoading ? "Loading..." : placeholder}
            </span>
            <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
          </Button>
        </PopoverTrigger>
        <PopoverContent className="w-[--radix-popover-trigger-width] min-w-[220px] p-0">
          <Command>
            <CommandInput
              placeholder="Search..."
              value={search}
              onValueChange={setSearch}
            />
            <CommandList>
              <CommandEmpty>
                {isLoading ? "Loading..." : "No options found."}
              </CommandEmpty>
              <CommandGroup>
                {options.map((o) => (
                  <CommandItem
                    key={o.value}
                    value={`${o.label} ${o.value}`}
                    onSelect={() => { toggle(o.value); }}
                  >
                    <Check
                      className={cn(
                        "mr-2 h-4 w-4",
                        value.includes(o.value) ? "opacity-100" : "opacity-0",
                      )}
                    />
                    <span className="truncate">{o.label}</span>
                  </CommandItem>
                ))}
                {showCreate && (
                  <CommandItem
                    value={`__custom_${search}`}
                    onSelect={() => {
                      toggle(search.trim());
                      setSearch("");
                    }}
                  >
                    Add "{search.trim()}"
                  </CommandItem>
                )}
              </CommandGroup>
            </CommandList>
          </Command>
        </PopoverContent>
      </Popover>
    </div>
  );
}
